import React from 'react';
import PropTypes from 'prop-types';

const linkify = (text, keyPrefix) => {
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`/g;
  let lastIndex = 0;
  let match;
  let partKey = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1] && match[2]) {
      parts.push(
        <a href={match[2]} key={`${keyPrefix}-a-${partKey}`}>
          {match[1]}
        </a>
      );
    } else if (match[3]) {
      parts.push(<strong key={`${keyPrefix}-s-${partKey}`}>{match[3]}</strong>);
    } else if (match[4]) {
      parts.push(<code key={`${keyPrefix}-c-${partKey}`}>{match[4]}</code>);
    }

    partKey += 1;
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length ? parts : text;
};

const MarkdownBody = ({ markdown, midImage }) => {
  const lines = markdown.split('\n');
  const nodes = [];
  let i = 0;
  let insertedMidImage = false;
  let headingCount = 0;

  const maybeInsertMidImage = () => {
    if (!midImage || insertedMidImage || headingCount < 2) {
      return;
    }
    insertedMidImage = true;
    nodes.push(
      <figure className={midImage.className} key='mid-image'>
        <img src={midImage.src} alt={midImage.alt} />
        {(midImage.caption || midImage.credit) && (
          <figcaption>
            {midImage.caption}
            {midImage.credit ? ` Photo: ${midImage.credit}.` : ''}
          </figcaption>
        )}
      </figure>
    );
  };

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim() || line.trim() === '---') {
      i += 1;
      continue;
    }

    if (line.startsWith('### ')) {
      headingCount += 1;
      maybeInsertMidImage();
      nodes.push(<h3 key={`h3-${i}`}>{linkify(line.slice(4), `h3-${i}`)}</h3>);
      i += 1;
      continue;
    }

    if (line.startsWith('## ')) {
      headingCount += 1;
      maybeInsertMidImage();
      nodes.push(<h2 key={`h2-${i}`}>{linkify(line.slice(3), `h2-${i}`)}</h2>);
      i += 1;
      continue;
    }

    if (line.startsWith('> ')) {
      const quoteLines = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        quoteLines.push(lines[i].slice(2));
        i += 1;
      }
      nodes.push(
        <blockquote key={`q-${i}`}>{linkify(quoteLines.join(' '), `q-${i}`)}</blockquote>
      );
      continue;
    }

    if (/^[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(
          <li key={`uli-${i}`}>{linkify(lines[i].replace(/^[-*] /, ''), `uli-${i}`)}</li>
        );
        i += 1;
      }
      nodes.push(<ul key={`ul-${i}`}>{items}</ul>);
      continue;
    }

    if (/^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(
          <li key={`oli-${i}`}>{linkify(lines[i].replace(/^\d+\. /, ''), `oli-${i}`)}</li>
        );
        i += 1;
      }
      nodes.push(<ol key={`ol-${i}`}>{items}</ol>);
      continue;
    }

    // Related section at end is handled by the article template
    if (line.startsWith('### Related') || line.trim() === '### Related') {
      break;
    }

    const paragraph = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      lines[i].trim() !== '---' &&
      !lines[i].startsWith('#') &&
      !lines[i].startsWith('> ') &&
      !/^[-*] /.test(lines[i]) &&
      !/^\d+\. /.test(lines[i])
    ) {
      if (lines[i].startsWith('### Related')) {
        break;
      }
      paragraph.push(lines[i]);
      i += 1;
    }

    if (paragraph.length) {
      nodes.push(<p key={`p-${i}`}>{linkify(paragraph.join(' '), `p-${i}`)}</p>);
    }
  }

  return <>{nodes}</>;
};

MarkdownBody.propTypes = {
  markdown: PropTypes.string.isRequired,
  midImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    caption: PropTypes.string,
    credit: PropTypes.string,
    className: PropTypes.string
  })
};

MarkdownBody.defaultProps = {
  midImage: null
};

export default MarkdownBody;
